import {
  CURRENCY_TO_COUNTRY,
  CURRENCY_TO_FLAG_CODE,
} from "@/modules/dashboard/wallets/constants/wallets.constants";

import { CURRENCY_SYMBOLS } from "../constants/dispersions.constants";

/**
 * Display helpers for a currency on its own, without a country.
 *
 * The dispersion origin is a wallet, so there is no country to borrow a symbol or a flag from. These read the
 * wallets module's tables — that import direction is already established (dispersions imports wallets, never
 * the reverse).
 */
export function currencySymbol(currency: string): string {
  return CURRENCY_SYMBOLS[currency] ?? "";
}

/** E.g. "USD · Estados Unidos · Ecuador · Panamá". Falls back to the code alone. */
export function currencyLabel(currency: string): string {
  const country = CURRENCY_TO_COUNTRY[currency];
  return country ? `${currency} · ${country}` : currency;
}

/** ISO2 for a flag component, or null when the currency spans several countries. */
export function currencyFlagCode(currency: string): string | null {
  return CURRENCY_TO_FLAG_CODE[currency] ?? null;
}

/**
 * Resolves the `?origin_currency=` deep-link param to a wallet currency.
 *
 * Accepts a currency code directly, and — for one release — the ISO3 country the wallet card used to pass, so
 * live links and bookmarks keep working. Returns null for anything unrecognized rather than guessing.
 */
export function resolveOriginCurrencyParam(
  param: string,
  countries: { code: string; currency: string }[],
): string | null {
  const wanted = param.trim().toUpperCase();
  if (!wanted) return null;
  if (countries.some((c) => c.currency.toUpperCase() === wanted)) return wanted;
  const byCountry = countries.find((c) => c.code.toUpperCase() === wanted);
  return byCountry ? byCountry.currency.toUpperCase() : null;
}

/** Resolves the `?destination=` deep-link param (an ISO3 corridor) against the wizard's catalog. */
export function resolveDestinationParam(param: string, countries: { code: string }[]): string | null {
  const wanted = param.trim().toUpperCase();
  if (!wanted) return null;
  return countries.find((c) => c.code.toUpperCase() === wanted)?.code ?? null;
}
